import { FC } from 'react';
import { NetworkStatus } from '@apollo/client';
import { Box } from '@chakra-ui/react';

import { useBankOverviewSearchParams } from '../hooks/useBankOverviewSearchParams';
import { useShouldMergeIntraCompanyFundsTransfers } from '../hooks/useShouldMergeIntraCompanyFundsTransfers';
import AdvancedSearch from './AdvancedSearch';
import EmptyState from './EmptyState';
import { toTransactionList } from './toTransactionList';
import TransactionList from './TransactionList';
import { TransactionsSearchConnection } from './TransactionList/types';

interface TransactionsProps {
  filtered: boolean;
  transactions?: TransactionsSearchConnection;
  loadMore: () => void;
  networkStatus?: NetworkStatus;
  loading: boolean;
  isAllAccounts?: boolean;
}

const defaultTransactions: TransactionsSearchConnection = {
  __typename: 'TransactionsSearchConnection',
  edges: [],
  pageInfo: {
    __typename: 'TransactionsSearchPageInfo',
    hasNextPage: false,
    nextCursor: null,
    totalCount: 0,
  },
};

const Transactions: FC<TransactionsProps> = ({
  filtered,
  loading,
  loadMore,
  networkStatus,
  transactions = defaultTransactions,
}) => {
  const { bankAccountId } = useBankOverviewSearchParams();
  const mergeIntraCompanyFundsTransfers =
    useShouldMergeIntraCompanyFundsTransfers();

  const hasMoreResults = transactions.pageInfo.hasNextPage;

  const isAllAccounts = !bankAccountId;

  const sections = toTransactionList({
    mergeIntraCompanyFundsTransfers,
    transactions,
  });

  return (
    <Box position="relative">
      <AdvancedSearch
        totalCount={
          isAllAccounts
            ? sections.flatMap((section) => section.data).length
            : transactions.pageInfo.totalCount
        }
      />
      {!filtered && !loading && transactions.edges.length === 0 ? (
        <EmptyState />
      ) : (
        <TransactionList
          hasMoreResults={hasMoreResults}
          isFetchingMore={networkStatus === 3}
          loading={loading}
          loadMore={loadMore}
          sections={sections}
        />
      )}
    </Box>
  );
};

export default Transactions;
